<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-4">
        <page-header>HubSpot</page-header>

        <vx-expandable-card>
            <template v-slot:header> HubSpot Integration </template>
            <template v-slot:headerRight>
                <div v-if="connectionStatus === 'loading'">
                    <vx-badge color="slate"> Loading </vx-badge>
                </div>
                <div v-else-if="connectionStatus === 'connected'">
                    <vx-badge color="success"> Connected </vx-badge>
                </div>
                <div v-else-if="connectionStatus === 'broken'">
                    <vx-badge color="orange"> Needs reconnection </vx-badge>
                </div>
                <div v-else>
                    <vx-badge color="slate"> Not connected </vx-badge>
                </div>
            </template>

            <template v-slot:content>
                <div v-if="!hasPermission" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner can configure integrations.
                </div>
                <div v-else-if="connectionStatus === 'connected'" class="flex flex-col gap-6 mt-3">
                    <div class="mb-4">Your HubSpot account is connected to Voxie.</div>

                    <div class="mb-4">
                        Click the button below to disconnect your HubSpot account from your Voxie team.
                    </div>

                    <vx-button
                        color="danger"
                        size="lg"
                        @click="disconnectHubspot"
                        :disabled="disconnecting"
                        data-test="disconnect-button"
                    >
                        {{ disconnecting ? 'Disconnecting...' : 'Disconnect HubSpot account' }}
                    </vx-button>
                </div>
                <div v-else-if="connectionStatus === 'broken'" class="flex flex-col gap-6 mt-3">
                    <div class="mb-4">
                        Your HubSpot account needs to be reconnected. This can happen if the connection token has
                        expired or if there are changes in your HubSpot account.
                    </div>

                    <vx-button size="lg" href="/integrations/hubspot/oauth/initiate">
                        Connect HubSpot account
                    </vx-button>
                </div>
                <div v-else-if="connectionStatus === 'not_connected'" class="flex flex-col gap-6 mt-3">
                    <div class="mb-4">Click the button below to connect your HubSpot account with your Voxie team.</div>

                    <vx-button size="lg" href="/integrations/hubspot/oauth/initiate">
                        Connect HubSpot account
                    </vx-button>
                </div>
            </template>
        </vx-expandable-card>
    </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { VxExpandableCard, VxButton, VxBadge, VxSwal } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import teamsClient from '~/services/teamsClient';
import { useToasted } from '~/composables/useToasted';

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const connectionStatus = ref('loading');
const disconnecting = ref(false);

onMounted(async () => {
    const response = await teamsClient.getHubspotConnectionStatus(Spark.state.currentTeam.id);
    connectionStatus.value = response.data.status;
});

const hasPermission = computed(() => props.ownsTeam);

const disconnectHubspot = () => {
    VxSwal.confirmation(
        'Disconnect HubSpot',
        'Are you sure you want to disconnect your HubSpot account? This action cannot be undone.',
        'danger',
        async () => {
            disconnecting.value = true;
            try {
                await teamsClient.disconnectHubspot(Spark.state.currentTeam.id);
                connectionStatus.value = 'not_connected';
                toasted.success('HubSpot account disconnected successfully.');
            } catch (error) {
                console.error(error);
                toasted.error('Failed to disconnect HubSpot account. Please try again.');
            } finally {
                disconnecting.value = false;
            }
        }
    );
};
</script>
